import React from "react"
import {Box, Typography, Chip, Button} from "@mui/material"
import {format} from "date-fns"

import type {ConversationMessage} from "@indebted/api"

function formatOccurredAt(occurredAt: string): string {
	return format(new Date(occurredAt), "d MMM yyyy, HH:mm")
}

function directionChipProps(direction: string): {label: string; color: "secondary" | "primary" | "info" | "warning"} {
	switch (direction) {
		case "inbound":
			return {label: "Customer", color: "secondary"}
		case "agent-reply":
			return {label: "Agent reply", color: "info"}
		case "information-request":
			return {label: "Information request", color: "warning"}
		default:
			return {label: "Auto-reply", color: "primary"}
	}
}

function deliveryChipProps(status: string): {label: string; color: "success" | "warning" | "error"} | null {
	switch (status) {
		case "delivered":
			return {label: "Delivered", color: "success"}
		case "temporarily-undelivered":
			return {label: "Delivery delayed", color: "warning"}
		case "permanently-undelivered":
			return {label: "Not delivered", color: "error"}
		default:
			return null
	}
}

function SMSMessageRow({
	message,
	isAnchor,
	isReplyTarget,
	targetIsEarlier,
	onDraftReply,
}: {
	message: ConversationMessage
	isAnchor: boolean
	isReplyTarget: boolean
	targetIsEarlier: boolean
	onDraftReply?: () => void
}) {
	const {label: directionLabel, color: directionColor} = directionChipProps(message.direction)
	const deliveryChip = message.deliveryStatus ? deliveryChipProps(message.deliveryStatus) : null

	const headerChip =
		isReplyTarget && targetIsEarlier ? (
			<Chip label="Replying to this" size="small" color="primary" />
		) : isAnchor ? (
			targetIsEarlier ? (
				<Chip label="Latest message" size="small" />
			) : (
				<Chip label="Reply anchor" size="small" color="primary" />
			)
		) : null

	return (
		<Box
			component="li"
			p={3}
			border="1px solid"
			borderColor={isReplyTarget ? "primary.main" : "grey.200"}
			borderRadius={1}
			sx={{listStyle: "none"}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
				<Chip label={directionLabel} size="small" color={directionColor} />
				{headerChip}
			</Box>
			<Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
				<Typography variant="caption" color="textSecondary">
					{formatOccurredAt(message.occurredAt)}
					{message.direction === "agent-reply" && message.authoredBy && ` · by ${message.authoredBy}`}
				</Typography>
				{message.direction === "inbound" && message.classificationCategory && (
					<Chip label={message.classificationCategory} size="small" variant="outlined" />
				)}
				{message.direction === "agent-reply" && message.edited && (
					<Chip label="Edited" size="small" variant="outlined" />
				)}
				{deliveryChip && (
					<>
						<Chip label={deliveryChip.label} size="small" color={deliveryChip.color} variant="outlined" />
						{message.deliveryStatus === "delivered" && message.deliveryOccurredAt && (
							<Typography variant="caption" color="textSecondary">
								at {formatOccurredAt(message.deliveryOccurredAt)}
							</Typography>
						)}
						{message.deliveryStatus !== "delivered" && message.deliveryReason && (
							<Typography variant="caption" color="textSecondary">
								· {message.deliveryReason}
							</Typography>
						)}
					</>
				)}
			</Box>
			{message.body && (
				<Typography variant="body2" color="textPrimary" mt={1} sx={{whiteSpace: "pre-wrap"}}>
					{message.body}
				</Typography>
			)}
			{onDraftReply && (
				<Box mt={1}>
					<Button variant="outlined" size="small" onClick={onDraftReply} disabled={isReplyTarget}>
						{isReplyTarget ? "Drafting reply" : "Draft reply"}
					</Button>
				</Box>
			)}
		</Box>
	)
}

export {SMSMessageRow}
